import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '../../../../../cdl/Button/Button';
import { DropdownMenu, DropdownMenuOption } from '../../../../../cdl/DropdownMenu/DropdownMenu';
import { IconButton } from '../../../../../cdl/IconButton/IconButton';
import { useToasts } from '../../../../../cdl/Toast/useToasts';
import { queries } from '../../../../../common/api/queryKeys/queries';
import { ButtonGroup } from '../../../../../common/buttons/ButtonGroup';
import { CancelPopup } from '../../../../../common/CancelPopup/CancelPopup';
import { translate } from '../../../../../common/helpers/translate.helper';
import { useRole } from '../../../../../common/hooks/useRole';
import { IconDots } from '../../../../../common/icons/cdl/Dots';
import { SimpleConfirmPopup } from '../../../../../offer/detail/common/components/SimpleConfirmPopup';
import { isAdminReopenableOffer } from '../../../../../offer/detail/common/helpers/isAdminReopenableOffer';
import { useMessagesExport } from '../../../../../offer/detail/common/hooks/useMessagesExport';
import { useOfferReopen } from '../../../../../offer/detail/common/hooks/useOfferReopen';
import { useOfferUpdate } from '../../../../../offer/detail/common/hooks/useOfferUpdate';
import { OfferState } from '../../../../../offer/model/OfferState';
import { OfferActionsGrid } from '../../../common/OfferCard/OfferActionsGrid';
import { RfqOfferStatus } from '../../../components/RfqOfferCard/components/RfqOfferStatus';
import { useRfqOfferCardContext } from '../../../components/RfqOfferCard/hooks/useRfqOfferCardContext';

export const RfqOfferActions = () => {
    const { offer } = useRfqOfferCardContext();
    const queryClient = useQueryClient();
    const { id: rfqId } = useParams({ from: '/_app/requests-for-quotes/$id' });
    const [showCancelPopup, setShowCancelPopup] = useState(false);
    const [showReopenPopup, setShowReopenPopup] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const role = useRole();

    const updateOfferMutation = useOfferUpdate();
    const reopenOfferMutation = useOfferReopen();
    const { downloadMessages } = useMessagesExport(offer.id);
    const { addErrorToast } = useToasts();

    const isAllowedToEdit = role.isSupplier() && role.hasWriteRights(offer.supplierId);

    const onCancelPopupSubmit = async (cancelReason: string) => {
        await updateOfferMutation.mutateAsync(
            {
                offer: {
                    ...offer,
                    state: OfferState.ENQUIRY_DECLINED,
                    cancelReason,
                },
                offerId: offer.id,
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: queries.requestsForQuotes.detail(rfqId).queryKey });
                    setShowCancelPopup(false);
                },
                onError: () => {
                    addErrorToast(translate('global.genericToastError'));
                },
            }
        );
    };

    const onReopenPopupSubmit = async () => {
        await reopenOfferMutation.mutateAsync(
            { offerId: offer.id, requestForQuoteId: rfqId },
            {
                onSuccess: () => {
                    setShowReopenPopup(false);
                },
                onError: () => {
                    addErrorToast(translate('global.genericToastError'));
                },
            }
        );
    };

    const options: DropdownMenuOption[] = [
        {
            label: translate('offer.detail.header.actions.export.messages'),
            onSelect: () => downloadMessages(offer.offerNumber),
        },
    ];

    if (isAllowedToEdit) {
        options.push({
            label: translate('offer.detail.header.actions.cancelPopup.quoted.button'),
            onSelect: () => setShowCancelPopup(true),
            destructive: true,
        });
    }

    if (role.isSuperAdmin() && isAdminReopenableOffer(offer)) {
        options.push({
            label: translate('offer.detail.header.actions.reopenEnquiry'),
            onSelect: () => setShowReopenPopup(true),
        });
    }

    const isEnquiry = offer.state === OfferState.ENQUIRY;
    const isOrdered = offer.isOrder();
    const primaryAction = (() => {
        if (isEnquiry) {
            return {
                to: '/offer/$id/quote' as const,
                label: translate('rfq.detail.updateQuote'),
            };
        }

        if (isOrdered) {
            return {
                to: '/offer/$id' as const,
                label: translate('rfq.detail.showOrder'),
            };
        }

        return {
            to: '/offer/$id/edit' as const,
            label: translate('rfq.detail.edit'),
        };
    })();

    return (
        <>
            <OfferActionsGrid>
                <RfqOfferStatus offer={offer} isClosed={offer.isFinished()} />

                {isAllowedToEdit && !offer.isFinished() ? (
                    <ButtonGroup>
                        <Button
                            width="100%"
                            emphasis={isEnquiry || isOrdered ? 'high' : undefined}
                            onClick={() =>
                                navigate({
                                    to: primaryAction.to,
                                    params: { id: offer.id },
                                    state: { originLocation: location },
                                })
                            }
                        >
                            {primaryAction.label}
                        </Button>
                        {!offer.isOrder() ? (
                            <div>
                                <DropdownMenu
                                    align="end"
                                    options={options}
                                    trigger={<IconButton emphasis="medium" Icon={IconDots} />}
                                />
                            </div>
                        ) : null}
                    </ButtonGroup>
                ) : null}

                {role.isSuperAdmin() ? (
                    <ButtonGroup>
                        <Button
                            width="100%"
                            onClick={() =>
                                navigate({
                                    to: '/offer/$id',
                                    params: { id: offer.id },
                                })
                            }
                        >
                            {translate('rfq.detail.viewOffer')}
                        </Button>
                        <div>
                            <DropdownMenu
                                align="end"
                                options={options}
                                trigger={<IconButton emphasis="medium" Icon={IconDots} />}
                            />
                        </div>
                    </ButtonGroup>
                ) : null}
            </OfferActionsGrid>

            {showCancelPopup ? (
                <CancelPopup
                    isOpen={showCancelPopup}
                    onSubmit={({ cancelReason }) => onCancelPopupSubmit(cancelReason)}
                    onDismiss={() => setShowCancelPopup(false)}
                    isSubmitting={updateOfferMutation.isPending}
                    text={{
                        headline: translate('offer.detail.header.actions.cancelPopup.quoted.headline'),
                        description: translate('offer.detail.header.actions.cancelPopup.quoted.description'),
                        buttonText: translate('offer.detail.header.actions.cancelPopup.quoted.button'),
                    }}
                />
            ) : null}

            {showReopenPopup ? (
                <SimpleConfirmPopup
                    isOpen={showReopenPopup}
                    onSubmit={onReopenPopupSubmit}
                    onDismiss={() => setShowReopenPopup(false)}
                    isSubmitting={reopenOfferMutation.isPending}
                    headline={translate('offer.detail.header.actions.reopenEnquiryPopup.headline')}
                    description={translate('offer.detail.header.actions.reopenEnquiryPopup.description')}
                    buttonText={translate('offer.detail.header.actions.reopenEnquiryPopup.button')}
                />
            ) : null}
        </>
    );
};
